import { Request, Response, NextFunction } from 'express';
import { ZodSchema, ZodError } from 'zod';

// Extend Express Request type to include user property
declare global {
    namespace Express {
        interface Request {
            user?: {
                id: string;
                email: string;
                roles?: string[];
            };
        }
    }
}

/**
 * Validation Middleware Factory
 * 
 * Creates Express middleware that validates requests against Zod schemas.
 * Enforces the API boundary by rejecting invalid input for DDD operations.
 */
export function validateRequest(schema: {
    body?: ZodSchema;
    query?: ZodSchema;
    params?: ZodSchema;
}) {
    return (req: Request, res: Response, next: NextFunction) => {
        try {
            // Validate request body
            if (schema.body) {
                req.body = schema.body.parse(req.body);
            }

            // Validate query parameters
            if (schema.query) {
                req.query = schema.query.parse(req.query);
            }

            // Validate route parameters
            if (schema.params) {
                req.params = schema.params.parse(req.params);
            }

            next();
        } catch (error) {
            if (error instanceof ZodError) {
                return res.status(400).json({
                    error: 'Validation Error',
                    message: 'DDD operation validation failed',
                    details: error.errors.map(err => ({
                        field: err.path.join('.'),
                        message: err.message,
                        code: err.code,
                        received: (err as any).received
                    })),
                    timestamp: new Date().toISOString(),
                    requestId: req.headers['x-request-id'] || 'unknown'
                });
            }

            // Handle other validation errors
            return res.status(400).json({
                error: 'Bad Request',
                message: 'Invalid request format for DDD operation',
                timestamp: new Date().toISOString(),
                requestId: req.headers['x-request-id'] || 'unknown'
            });
        }
    };
}

/**
 * Parameter Validation Schemas for DDD Operations
 */
import { z } from 'zod';

export const DomainParamsSchema = z.object({
    domainId: z.string().uuid('Invalid domain ID format')
});

export const BoundedContextParamsSchema = z.object({
    contextId: z.string().uuid('Invalid bounded context ID format')
});

export const ProjectParamsSchema = z.object({
    projectId: z.string().uuid('Invalid project ID format')
});

export const DomainContextParamsSchema = z.object({
    domainId: z.string().uuid('Invalid domain ID format'),
    contextId: z.string().uuid('Invalid bounded context ID format')
});

/**
 * Common validation middleware instances for Strategic Design
 */
export const validateDomainParams = validateRequest({
    params: DomainParamsSchema
});

export const validateBoundedContextParams = validateRequest({
    params: BoundedContextParamsSchema
});

export const validateProjectParams = validateRequest({
    params: ProjectParamsSchema
});

export const validateDomainContextParams = validateRequest({
    params: DomainContextParamsSchema
});

/**
 * Error Response Helper
 * 
 * Standardizes error responses for DDD operations
 */
export function createErrorResponse(
    error: string,
    message: string,
    statusCode: number = 400,
    details?: any[]
) {
    return {
        error,
        message,
        details,
        timestamp: new Date().toISOString(),
        domain: 'Strategic Design'
    };
}